import React, { useState, useEffect, useCallback } from 'react';
import { Edit, ChevronLeftIcon, ChevronRightIcon, } from 'lucide-react'; // Import the Edit icon
import TagAddForm from './TagAddForm';
import TagEditForm from './TagEditForm';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { useApiCaller } from '../utils/ApiUtilities';
import { getTagUrl, getTagsSyncUrl, getTagReferencesUrl } from '../utils/UrlUtilities';
import { constructTagDeletePayload, constructTagReferencesPayload } from '../utils/PayloadUtils';
import { TAGLEVELS, TAGTYPE, itemsPerPageOptions } from '../utils/Constants';
import { isEmpty } from '../utils/BasicUtilities';


// Mock for Input component (assuming it's a styled input)
const Input = ({ type, placeholder, value, onChange, className, id, min, max, required, 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy, disabled }) => (
    <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`form-control ${className || ''}`}
        id={id}
        min={min}
        max={max}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
    />
);

// Mock for Checkbox component (assuming it's a styled checkbox)
const Checkbox = ({ onCheckedChange, checked }) => (
    <input
        type="checkbox"
        className="form-check-input"
        onChange={e => onCheckedChange(e)}
        checked={checked}
    />
);

// --- End Mocked Dependencies ---

const defaultPageSize = 200;

export default function TagGrid() {
    const [tags, setTags] = useState([]);
    // States for filter inputs (pending values)
    const [pendingSearchTerm, setPendingSearchTerm] = useState('');
    const [pendingFilterLevel, setPendingFilterLevel] = useState('');
    const [pendingFilterType, setPendingFilterType] = useState('');

    // States for applied filters (trigger API call)
    const [searchTerm, setSearchTerm] = useState('');
    const [filterLevel, setFilterLevel] = useState('');
    const [filterType, setFilterType] = useState('');

    const [selectedRows, setSelectedRows] = useState([]);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [showAddTagForm, setShowAddTagForm] = useState(false);
    const [showEditTagForm, setShowEditTagForm] = useState(false);
    const [editingTag, setEditingTag] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [mutationsEnabled, setMutationsEnabled] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');
    const [syncSkipped, setSyncSkipped] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteReferences, setDeleteReferences] = useState(null);
    const CallApi = useApiCaller();

    const fetchTags = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const queryParams = {
            };
            if (!isEmpty(searchTerm)) {
                queryParams["name"] = searchTerm; // Use applied search term
            }
            if (!isEmpty(filterLevel)) {
                queryParams["level"] = filterLevel; // Use applied level filter
                
            }
            if (!isEmpty(filterType)) {
                queryParams["tag_type"] = filterType; // Use applied type filter
            }

            const tagUrl = getTagUrl(queryParams);
            const tagGetResponse = await CallApi({
                url: tagUrl,
                method: 'GET',
                token: true,
                payload: null,
            });
            if (tagGetResponse.success) {
                setTags(tagGetResponse.data.tags);
                setCurrentPage(1); // Reset to first page on new filter/search
            } else {
                setTags([]);
                setError(tagGetResponse.message || "Failed to fetch tags.");
            }
        } catch (error) {
            setTags([]);
            setError("An error occurred while fetching tags.");
            console.error("Fetch Tags Error:", error);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, filterLevel, filterType]);

    useEffect(() => {
        fetchTags(); // Initial fetch and refetch when applied filter states change
    }, [searchTerm, filterLevel, filterType]);

    // Live filtering across ALL tags (server-side): debounce the text search and apply the
    // level/type dropdowns immediately, so results aren't limited to the current page and don't
    // wait on the Apply button.
    useEffect(() => {
        const t = setTimeout(() => setSearchTerm(pendingSearchTerm), 350);
        return () => clearTimeout(t);
    }, [pendingSearchTerm]);

    // Show the grid rows newest-added → oldest (by id). Sorted client-side so the app-wide
    // /tags dropdowns keep their alphabetical (tag_type, name) order.
    const displayedTags = [...tags].sort((a, b) => (b.tag_id || 0) - (a.tag_id || 0));
    const totalPages = Math.ceil(displayedTags.length / pageSize);
    const paginatedTags = displayedTags.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    // Handlers for pending filter changes
    const handlePendingSearchChange = (e) => {
        setPendingSearchTerm(e.target.value);
    };

    const handlePendingLevelChange = (e) => {
        setPendingFilterLevel(e.target.value);
        setFilterLevel(e.target.value); // apply immediately across all tags
    };

    const handlePendingTypeChange = (e) => {
        setPendingFilterType(e.target.value);
        setFilterType(e.target.value); // apply immediately across all tags
    };

    const handleApplyFilters = () => {
        // Apply pending filters to actual filter states, which triggers fetchTags
        setSearchTerm(pendingSearchTerm);
        setFilterLevel(pendingFilterLevel);
        setFilterType(pendingFilterType);
        setSelectedRows([]); // Clear selections on new filter application
    };

    const handleClearFilters = () => {
        // Clear both pending and applied filters
        setPendingSearchTerm('');
        setPendingFilterLevel('');
        setPendingFilterType('');
        setSearchTerm('');
        setFilterLevel('');
        setFilterType('');
        setSelectedRows([]); // Clear any selections when filters are cleared
        // fetchTags will be called by useEffect due to applied filter state changes
    };

    const requestDelete = async () => {
        if (selectedRows.length === 0) return;
        setError(null);
        try {
            // Fetch per-tag reference counts so the dialog can warn / block.
            const refResponse = await CallApi({
                url: getTagReferencesUrl(),
                method: 'POST',
                payload: constructTagReferencesPayload(selectedRows),
                token: true,
            });
            if (refResponse.success) {
                const refs = (refResponse.data?.references || []).map(r => ({
                    label: `${r.name} (${r.tag_type}, level=${r.level})`,
                    question_count: r.question_count,
                    quiz_count: r.quiz_count,
                }));
                setDeleteReferences(refs);
            } else {
                // If the references check failed, still let the admin try the delete
                // but with no per-tag info — Confirm will be enabled.
                setDeleteReferences([]);
            }
        } catch (e) {
            setDeleteReferences([]);
        }
        setConfirmOpen(true);
    };

    const performDelete = async () => {
        setDeleting(true);
        setError(null);
        try {
            const deleteTagUrl = getTagUrl();
            const deleteTagPayload = constructTagDeletePayload(selectedRows);
            const deleteTagResponse = await CallApi({
                url: deleteTagUrl,
                method: 'DELETE',
                payload: deleteTagPayload,
                token: true,
            });
            if (deleteTagResponse.success) {
                await fetchTags();
                setSelectedRows([]);
            } else {
                setError(deleteTagResponse.message || 'Failed to delete tags.');
            }
        } catch (error) {
            setError('An error occurred while deleting the tags.');
            console.error('Delete Tag Error:', error);
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
            setDeleteReferences(null);
        }
    };

    const cancelDelete = () => {
        setConfirmOpen(false);
        setDeleteReferences(null);
    };

    const handleSync = async () => {
        setIsSyncing(true);
        setError(null);
        setSyncMessage('');
        setSyncSkipped([]);
        try {
            const response = await CallApi({
                url: getTagsSyncUrl(),
                method: 'POST',
                token: true,
            });
            if (response.success) {
                const { created, deleted, skipped, total } = response.data || {};
                setSyncMessage(`Synced: ${created ?? 0} added, ${deleted ?? 0} removed (total ${total ?? 0})`);
                setSyncSkipped(skipped || []);
                await fetchTags();
            } else {
                setError(response.message || 'Failed to sync tags.');
            }
        } catch (e) {
            setError('An error occurred while syncing tags.');
        } finally {
            setIsSyncing(false);
        }
    };

    const handleEdit = (tag) => {
        setEditingTag(tag);
        setShowEditTagForm(true);
    };

    const handleCheckboxChange = (id, event) => {
        setSelectedRows(prev => {
            if (event.target.checked) {
                return [...prev, id];
            } else {
                return prev.filter(rowId => rowId !== id);
            }
        });
    };

    const handleSelectAllChange = (event) => {
        if (event.target.checked) {
            setSelectedRows(paginatedTags.map(tag => tag.tag_id));
        } else {
            setSelectedRows([]);
        }
    };

    const handlePageChange = (dir) => {
        setCurrentPage(prev => {
            if (totalPages === 0) return 1; // Handle case with no pages
            if (dir === 'prev') {
                return prev === 1 ? totalPages : prev - 1;
            } else if (dir === 'next') {
                return prev === totalPages ? 1 : prev + 1;
            }
            return prev;
        });
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1); // Reset to first page
    };

    const handleAdd = () => {
        setShowAddTagForm(true);
    };

    const handleCancelAddTag = () => {
        setShowAddTagForm(false);
    };

    const handleCancelEditTag = () => {
        setShowEditTagForm(false);
        setEditingTag(null);
    };

    const handleSaveTag = () => {
        setShowAddTagForm(false);
        setShowEditTagForm(false);
        setEditingTag(null);
        fetchTags(); // Re-fetch tags to see new/updated data
    };

    return (
        <div className={`container py-4 min-vh-100 bg-light ${ (showAddTagForm || showEditTagForm) ? 'position-relative' : ''}`}>
            {/* Main content of TagGrid */}
            <div className={`${(showAddTagForm || showEditTagForm) ? 'content-overlay' : ''}`}>
                <h1 className="fs-2 fw-bold text-dark mb-4">Tag Management</h1>

                {/* Fixed Filter Section */}
                <div className="card shadow-sm mb-4 kp-filter">
                    <div className="card-body">
                        <h5 className="card-title text-dark mb-3">Filter Tags</h5>
                        <div className="row g-3 align-items-end">
                            <div className="col-md-4">
                                <label htmlFor="searchTagName" className="form-label text-gray-700">Search by Tag Name</label>
                                <Input
                                    type="text"
                                    id="searchTagName"
                                    placeholder="e.g., Mathematics"
                                    value={pendingSearchTerm}
                                    onChange={handlePendingSearchChange}
                                    className="w-100"
                                />
                            </div>
                            <div className="col-md-3">
                                <label htmlFor="filterLevel" className="form-label text-gray-700">Level</label>
                                <select
                                    id="filterLevel"
                                    className="form-select"
                                    value={pendingFilterLevel}
                                    onChange={handlePendingLevelChange}
                                >
                                    <option value={""} >
                                                {"All Tags"}
                                    </option>
                                    {TAGLEVELS.map((item) => (
                                        item.submenu ? (
                                            <optgroup key={item.value} label={item.label}>
                                                {item.submenu.map((sub) => (
                                                    <option key={sub.value} value={sub.value}>
                                                        {sub.label}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ) : (
                                            <option key={item.value} value={item.value} disabled={item.value === ""}>
                                                {item.label}
                                            </option>
                                        )
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label htmlFor="filterType" className="form-label text-gray-700">Type</label>
                                <select
                                    id="filterType"
                                    className="form-select"
                                    value={pendingFilterType}
                                    onChange={handlePendingTypeChange}
                                >
                                    <option value={""}>
                                            {"All Type"}
                                        </option>
                                    {TAGTYPE.map(typeOption => (
                                        <option key={typeOption} value={typeOption}>
                                            {typeOption}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-2 d-flex gap-2">
                                <button onClick={handleApplyFilters} className="btn btn-primary flex-grow-1">
                                    Apply
                                </button>
                                <button onClick={handleClearFilters} className="btn btn-outline-secondary flex-grow-1">
                                    Clear
                                </button>
                            </div>
                        </div>
                        {(searchTerm || filterLevel || filterType) && (
                            <p className="mt-3 text-muted small">
                                Current Filters:
                                {searchTerm && ` Search: "${searchTerm}"`}
                                {searchTerm && (filterLevel || filterType) && <span className="mx-1 fw-bold">AND</span>}
                                {filterLevel && ` Level: "${TAGLEVELS.find(l => l.value === filterLevel)?.label}"`}
                                {filterLevel && filterType && <span className="mx-1 fw-bold">AND</span>}
                                {filterType && ` Type: "${TAGTYPE.find(t => t === filterType)}"`}
                            </p>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex flex-wrap align-items-center mb-3 gap-2">
                    <button
                        className="btn btn-primary"
                        onClick={handleSync}
                        disabled={isSyncing}
                    >
                        {isSyncing ? 'Syncing…' : 'Sync Tags'}
                    </button>
                    <div className="form-check ms-2 me-auto">
                        <input
                            id="enableMutations"
                            type="checkbox"
                            className="form-check-input"
                            checked={mutationsEnabled}
                            onChange={(e) => setMutationsEnabled(e.target.checked)}
                        />
                        <label className="form-check-label small" htmlFor="enableMutations">
                            Enable Add/Delete <span className="text-muted">— safety gate; check to allow Add Tag and Delete Selected</span>
                        </label>
                    </div>
                    <button
                        className="btn btn-success"
                        onClick={handleAdd}
                        disabled={!mutationsEnabled}
                    >
                        Add Tag
                    </button>
                    <button
                        className="btn btn-danger"
                        onClick={requestDelete}
                        disabled={!mutationsEnabled || selectedRows.length === 0}
                    >
                        Delete Selected ({selectedRows.length})
                    </button>
                </div>

                {syncMessage && (
                    <div className="alert alert-info py-2 mb-2">{syncMessage}</div>
                )}
                {syncSkipped.length > 0 && (
                    <div className="alert alert-warning mb-3">
                        <div className="fw-bold mb-1">
                            {syncSkipped.length} tag(s) were missing from JSON but kept because they're still in use:
                        </div>
                        <ul className="mb-0 ps-3">
                            {syncSkipped.map(s => (
                                <li key={`${s.tag_id}`}>
                                    <strong>{s.name}</strong> ({s.tag_type}, level={s.level}) — used by {s.question_count} question(s), {s.quiz_count || 0} test(s)
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Loading/Error State */}
                {loading && (
                    <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-secondary mt-2">Loading tags...</p>
                    </div>
                )}
                {error && (
                    <div className="alert alert-danger text-center" role="alert">
                        {error}
                    </div>
                )}

                {/* Tags Table */}
                {!loading && !error && (
                    <div className="card shadow-sm overflow-hidden">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th scope="col" className="p-2 text-center">
                                            <Checkbox
                                                onCheckedChange={handleSelectAllChange}
                                                checked={selectedRows.length > 0 && paginatedTags.length > 0 && selectedRows.length === paginatedTags.length}
                                            />
                                        </th>
                                        <th scope="col" className="p-2 text-start text-uppercase">Tag Name</th>
                                        <th scope="col" className="p-2 text-start text-uppercase">Level</th>
                                        <th scope="col" className="p-2 text-start text-uppercase">Type</th>
                                        <th scope="col" className="p-2 text-start text-uppercase">Syllabus</th>
                                        <th scope="col" className="p-2 text-center text-uppercase">Edit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedTags.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center text-muted py-3">No tags found matching your criteria.</td>
                                        </tr>
                                    ) : (
                                        paginatedTags.map(tag => (
                                            <tr key={tag.tag_id}>
                                                <td className="p-2 text-center">
                                                    <Checkbox
                                                        onCheckedChange={(e) => handleCheckboxChange(tag.tag_id, e)}
                                                        checked={selectedRows.includes(tag.tag_id)}
                                                    />
                                                </td>
                                                <td className="p-2 text-start"><span className="badge bg-info-subtle text-danger me-1 mb-1">{tag.name}</span></td>
                                                <td className="p-2 text-start"><span className="badge bg-primary-subtle text-danger me-1 mb-1">{tag.level}</span></td>
                                                <td className="p-2 text-start"><span className="badge bg-success-subtle text-danger me-1 mb-1">{tag.tag_type}</span></td>
                                                <td className="p-2 text-start">
                                                    {(tag.tag_type === 'SUBJECT' || tag.tag_type === 'TOPIC')
                                                        ? <span className={`badge ${tag.is_current === false ? 'bg-warning' : 'bg-success'}`}>{tag.is_current === false ? 'Retired' : 'Current'}</span>
                                                        : <span className="text-muted">—</span>}
                                                </td>
                                                <td className="p-2 text-center">
                                                    <button
                                                        className="btn btn-link p-0 text-primary"
                                                        onClick={() => handleEdit(tag)}
                                                        title="Edit Tag"
                                                    >
                                                        <Edit size={20} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Pagination Footer */}
                <div className="d-flex justify-content-between align-items-center mt-4">
                    {/* Items per page dropdown */}
                    <div className="d-flex align-items-center gap-2">
                        <label htmlFor="pageSizeSelect" className="form-label text-secondary mb-0">Items per page:</label>
                        <select
                            id="pageSizeSelect"
                            className="form-select form-select-sm w-auto"
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        >
                            {itemsPerPageOptions.map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                    </div>

                    {/* Pagination Controls */}
                    <div className="d-flex align-items-center gap-3">
                        <button
                            className="btn btn-outline-secondary rounded-pill"
                            onClick={() => handlePageChange('prev')}
                            disabled={totalPages <= 1 && totalPages !== 0}
                        >
                            <ChevronLeftIcon className="w-4 h-4 me-1" /> Previous
                        </button>
                        <span className="text-secondary">Page {currentPage} of {totalPages === 0 ? 1 : totalPages} · Total: {displayedTags.length}</span>
                        <button
                            className="btn btn-outline-secondary rounded-pill"
                            onClick={() => handlePageChange('next')}
                            disabled={totalPages <= 1 && totalPages !== 0}
                        >
                            Next <ChevronRightIcon className="w-4 h-4 ms-1" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Modals are rendered here, on top of the main content */}
            {showAddTagForm && (
                <TagAddForm
                    onCancel={handleCancelAddTag}
                    onSave={handleSaveTag}
                />
            )}

            {showEditTagForm && (
                <TagEditForm
                    tag={editingTag}
                    onCancel={handleCancelEditTag}
                    onSave={handleSaveTag}
                />
            )}

            <ConfirmDeleteDialog
                open={confirmOpen}
                title="Delete tags"
                references={deleteReferences}
                onConfirm={performDelete}
                onCancel={cancelDelete}
                busy={deleting}
            />

            {/* Optional: Add a CSS style block for content-overlay to make the background opaqued */}
            <style>
                {`
                .content-overlay {
                    filter: blur(2px) grayscale(50%); /* Example: blur and dim */
                    pointer-events: none; /* Prevent interaction with background */
                    opacity: 0.7; /* Further dimming */
                }

                /* Ensure modals are on top */
                .modal.d-block {
                    z-index: 1050; /* Bootstrap's modal-backdrop z-index is 1040 */
                }
                `}
            </style>
        </div>
    );
}
